import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookOpen, Plus, Pencil, Trash2, Loader2, Save, Check, Copy, ArrowUp, ArrowDown, Search, LayoutGrid, Table as TableIcon } from "lucide-react";
import { toast } from "sonner";

export default function Subjects() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", status: "Active", for_timetable: false, for_marksheet: false });

  // assignments: { classId: [subjectId, ...] }
  const [assignments, setAssignments] = useState({});
  const [assignLoading, setAssignLoading] = useState(false);

  // assign tab toolbar state
  const [query, setQuery] = useState("");
  const [filterMode, setFilterMode] = useState("all"); // all | assigned | unassigned
  const [view, setView] = useState("table"); // table | grid

  // Edit-assignments dialog
  const [editAssignClass, setEditAssignClass] = useState(null);
  const [draftIds, setDraftIds] = useState([]);
  const [savingAssign, setSavingAssign] = useState(false);

  // Copy-from dialog
  const [copyTarget, setCopyTarget] = useState(null);
  const [copyFromId, setCopyFromId] = useState("");
  const [copying, setCopying] = useState(false);

  const canManage = hasPerm(user, "manage_exams");
  const canReorder = hasPerm(user, "manage_settings");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/subjects").then((r) => setSubjects(r.data)).finally(() => setLoading(false));
  }, []);

  const loadAssignments = useCallback((cls) => {
    if (!cls.length) { setAssignments({}); return; }
    setAssignLoading(true);
    Promise.all(cls.map((c) => api.get(`/classes/${c.id}/subjects`).then((r) => [c.id, r.data.subject_ids || []]).catch(() => [c.id, []])))
      .then((pairs) => setAssignments(Object.fromEntries(pairs)))
      .finally(() => setAssignLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/classes").then((r) => { setClasses(r.data); loadAssignments(r.data); }).catch(() => {});
  }, [load, loadAssignments]);

  const openNew = () => { setEdit(null); setForm({ name: "", code: "", status: "Active", for_timetable: false, for_marksheet: false }); setOpen(true); };
  const openEdit = (s) => { setEdit(s); setForm({ name: s.name, code: s.code || "", status: s.status || "Active", for_timetable: !!s.for_timetable, for_marksheet: !!s.for_marksheet }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Subject name required"); return; }
    try {
      if (edit) await api.put(`/subjects/${edit.id}`, form);
      else await api.post("/subjects", form);
      toast.success("Subject saved"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete subject "${s.name}"?`)) return;
    try { await api.delete(`/subjects/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const clsLabel = (c) => `${c.name}${c.section ? `-${c.section}` : ""}`;
  const subjName = (id) => subjects.find((s) => s.id === id)?.name;

  // Edit assignments
  const openEditAssign = (c) => { setEditAssignClass(c); setDraftIds([...(assignments[c.id] || [])]); };
  const toggleDraft = (id) => setDraftIds((d) => d.includes(id) ? d.filter((x) => x !== id) : [...d, id]);
  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await api.put(`/classes/${editAssignClass.id}/subjects`, { subject_ids: draftIds });
      setAssignments((a) => ({ ...a, [editAssignClass.id]: [...draftIds] }));
      toast.success(`Subjects updated for ${clsLabel(editAssignClass)}`);
      setEditAssignClass(null);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingAssign(false); }
  };

  // Copy from
  const openCopy = (c) => { setCopyTarget(c); setCopyFromId(""); };
  const doCopy = async () => {
    if (!copyFromId) { toast.error("Select a class to copy from"); return; }
    const ids = [...(assignments[copyFromId] || [])];
    setCopying(true);
    try {
      await api.put(`/classes/${copyTarget.id}/subjects`, { subject_ids: ids });
      setAssignments((a) => ({ ...a, [copyTarget.id]: ids }));
      const src = classes.find((c) => c.id === copyFromId);
      toast.success(`Copied ${ids.length} subject${ids.length === 1 ? "" : "s"} from ${src ? clsLabel(src) : "class"}`);
      setCopyTarget(null); setCopyFromId("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setCopying(false); }
  };

  // Move class up/down (operates on global order, persisted)
  const move = async (globalIdx, dir) => {
    const j = globalIdx + dir;
    if (j < 0 || j >= classes.length) return;
    const arr = [...classes];
    [arr[globalIdx], arr[j]] = [arr[j], arr[globalIdx]];
    setClasses(arr);
    try { await api.put("/classes/reorder", { ordered_ids: arr.map((c) => c.id) }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const reorderable = canReorder && !query && filterMode === "all";
  const filtered = classes.filter((c) => {
    if (query && !clsLabel(c).toLowerCase().includes(query.toLowerCase())) return false;
    const cnt = (assignments[c.id] || []).length;
    if (filterMode === "assigned" && cnt === 0) return false;
    if (filterMode === "unassigned" && cnt > 0) return false;
    return true;
  });

  const flag = (on, testid) => (
    <td className="px-4 py-2.5 text-center" data-testid={testid}>{on ? <Check size={16} className="inline text-emerald-600" /> : <span className="text-slate-300">—</span>}</td>
  );

  const subjectChips = (ids) => ids.length === 0 ? <span className="text-slate-400">No subjects assigned</span> : (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((id) => subjName(id) && <span key={id} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">{subjName(id)}</span>)}
    </div>
  );

  const moveBtns = (c) => {
    const gi = classes.findIndex((x) => x.id === c.id);
    return (
      <div className="flex flex-col">
        <Button size="icon" variant="ghost" className="h-5 w-6" disabled={!reorderable || gi === 0} onClick={() => move(gi, -1)} data-testid={`move-up-${c.id}`} title={reorderable ? "Move up" : "Clear search/filter to reorder"}><ArrowUp size={13} /></Button>
        <Button size="icon" variant="ghost" className="h-5 w-6" disabled={!reorderable || gi === classes.length - 1} onClick={() => move(gi, 1)} data-testid={`move-down-${c.id}`} title={reorderable ? "Move down" : "Clear search/filter to reorder"}><ArrowDown size={13} /></Button>
      </div>
    );
  };

  const rowActions = (c) => canManage && (
    <div className="flex gap-2 justify-end">
      <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => openEditAssign(c)} data-testid={`edit-assign-btn-${c.id}`}><Pencil size={14} />Edit</Button>
      <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => openCopy(c)} data-testid={`copy-from-btn-${c.id}`}><Copy size={14} />Copy From</Button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><BookOpen className="text-indigo-500" size={26} />Subjects</h1>
          <p className="text-slate-500">Manage the global subject list and assign subjects to each class.</p>
        </div>
        {canManage && tab === "all" && <Button onClick={openNew} className="gap-1.5" data-testid="add-subject-btn"><Plus size={16} />Add Subject</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="subjects-tabs">
          <TabsTrigger value="all" data-testid="tab-all-subjects">All Subjects</TabsTrigger>
          <TabsTrigger value="assign" data-testid="tab-assign-subjects">Assign Subjects to Class</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-700">All Subjects ({subjects.length})</div>
            {loading ? <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
              subjects.length === 0 ? <div className="py-14 text-center text-slate-400">No subjects yet.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr><th className="px-4 py-2 text-left">Subject</th><th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-center">Timetable</th><th className="px-4 py-2 text-center">Marksheet</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2"></th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {subjects.map((s) => (
                      <tr key={s.id} data-testid={`subject-row-${s.id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{s.name}</td>
                        <td className="px-4 py-2.5 text-slate-500">{s.code || "—"}</td>
                        {flag(s.for_timetable, `subject-tt-flag-${s.id}`)}
                        {flag(s.for_marksheet, `subject-ms-flag-${s.id}`)}
                        <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{s.status}</span></td>
                        <td className="px-4 py-2.5 text-right">
                          {canManage && <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-subject-${s.id}`}><Pencil size={15} /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => remove(s)} data-testid={`delete-subject-${s.id}`}><Trash2 size={15} /></Button>
                          </div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </TabsContent>

        <TabsContent value="assign">
          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search class…" className="pl-8 h-9 w-52" data-testid="assign-search" />
              </div>
              <Select value={filterMode} onValueChange={setFilterMode}>
                <SelectTrigger className="h-9 w-40" data-testid="assign-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  <SelectItem value="assigned">With subjects</SelectItem>
                  <SelectItem value="unassigned">Without subjects</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden" data-testid="assign-view-switch">
              <button onClick={() => setView("table")} className={`h-9 px-3 flex items-center gap-1.5 text-sm ${view === "table" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="assign-view-table"><TableIcon size={15} />Table</button>
              <button onClick={() => setView("grid")} className={`h-9 px-3 flex items-center gap-1.5 text-sm ${view === "grid" ? "bg-indigo-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="assign-view-grid"><LayoutGrid size={15} />Grid</button>
            </div>
          </div>

          {assignLoading ? <div className="bg-white rounded-xl border border-slate-200 py-14 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
            classes.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-14 text-center text-slate-400">No classes yet.</div> :
            filtered.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-14 text-center text-slate-400">No classes match your search/filter.</div> :
            view === "table" ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm" data-testid="assign-class-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="px-2 py-2 w-10"></th>
                      <th className="px-4 py-2 text-left w-48">Class</th>
                      <th className="px-4 py-2 text-left">Subjects</th>
                      <th className="px-4 py-2 text-right w-56">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((c) => (
                      <tr key={c.id} data-testid={`assign-row-${c.id}`} className="align-top">
                        <td className="px-2 py-3">{moveBtns(c)}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{clsLabel(c)}</td>
                        <td className="px-4 py-3">{subjectChips(assignments[c.id] || [])}</td>
                        <td className="px-4 py-3 text-right">{rowActions(c)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="assign-class-grid">
                {filtered.map((c) => (
                  <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3" data-testid={`assign-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-slate-800">{clsLabel(c)}</div>
                      {moveBtns(c)}
                    </div>
                    <div className="min-h-[36px]">{subjectChips(assignments[c.id] || [])}</div>
                    {rowActions(c)}
                  </div>
                ))}
              </div>
            )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit subject dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="subject-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit Subject" : "Add Subject"}</DialogTitle><DialogDescription>Define a subject and where it should appear.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mathematics" data-testid="subject-name-input" className="mt-1" /></div>
            <div><Label>Code (optional)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. MATH" className="mt-1" /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="subject-for-timetable-label">
                <Checkbox checked={form.for_timetable} onCheckedChange={(v) => setForm({ ...form, for_timetable: !!v })} data-testid="subject-for-timetable" />
                <span className="text-sm text-slate-700">Subject for Timetable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="subject-for-marksheet-label">
                <Checkbox checked={form.for_marksheet} onCheckedChange={(v) => setForm({ ...form, for_marksheet: !!v })} data-testid="subject-for-marksheet" />
                <span className="text-sm text-slate-700">Subject for Marksheet</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-subject-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit assignments dialog */}
      <Dialog open={!!editAssignClass} onOpenChange={(o) => !o && setEditAssignClass(null)}>
        <DialogContent data-testid="edit-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Subjects — {editAssignClass ? clsLabel(editAssignClass) : ""}</DialogTitle>
            <DialogDescription>Select the subjects taught in this class.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-2 max-h-[55vh] overflow-auto">
            {subjects.length === 0 && <p className="text-sm text-slate-400">Create subjects first.</p>}
            {subjects.map((s) => (
              <label key={s.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`assign-check-row-${s.id}`}>
                <Checkbox checked={draftIds.includes(s.id)} onCheckedChange={() => toggleDraft(s.id)} data-testid={`assign-check-${s.id}`} />
                <span className="text-sm text-slate-700">{s.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAssignClass(null)}>Cancel</Button>
            <Button onClick={saveAssign} disabled={savingAssign} className="gap-1.5" data-testid="save-assign-btn">{savingAssign ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy from dialog */}
      <Dialog open={!!copyTarget} onOpenChange={(o) => !o && setCopyTarget(null)}>
        <DialogContent data-testid="copy-assign-dialog">
          <DialogHeader>
            <DialogTitle>Copy Subjects — into {copyTarget ? clsLabel(copyTarget) : ""}</DialogTitle>
            <DialogDescription>Replace this class's subjects with those of another class.</DialogDescription>
          </DialogHeader>
          <div className="py-1 space-y-2">
            <Select value={copyFromId} onValueChange={setCopyFromId}>
              <SelectTrigger data-testid="copy-source-select"><SelectValue placeholder="Copy from class" /></SelectTrigger>
              <SelectContent>{classes.filter((c) => copyTarget && c.id !== copyTarget.id).map((c) => <SelectItem key={c.id} value={c.id}>{clsLabel(c)} ({(assignments[c.id] || []).length})</SelectItem>)}</SelectContent>
            </Select>
            {copyFromId && <p className="text-xs text-slate-500">{(assignments[copyFromId] || []).length} subject(s) will be copied, replacing the current assignment.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyTarget(null)}>Cancel</Button>
            <Button onClick={doCopy} disabled={copying} className="gap-1.5" data-testid="copy-apply-btn">{copying ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}Copy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
